import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import ReportFilterSection from '../components/ReportFilterSection';
import { generateCompanyReportPDF, generatePartyReportPDF, generateFilteredReportPDF } from '../services/pdfService';
import FYPicker from '../components/FYPicker';
import { getCurrentFY } from '../utils/financialYear';
import { FileDown, Users, BarChart3, FileText, Wallet, Clock, ChevronDown, ChevronUp, Building2 } from 'lucide-react';

// Mobile-compatible PDF download helper
const downloadPDF = (doc, fileName) => {
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (isMobile) {
    window.open(url, '_blank');
  } else {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// Mobile-compatible blob download helper
const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (isMobile) {
    window.open(url, '_blank');
  } else {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// Utility function to format date as DD/MM/YYYY
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Pagination Component
const PaginationControls = ({ pagination, onPageChange, className = "" }) => {
  if (pagination.totalPages <= 1) return null;

  return (
    <div className={`px-4 py-3 flex items-center justify-between border-t border-brand-100 sm:px-6 ${className}`}>
      {/* Mobile Pagination */}
      <div className="flex sm:hidden justify-between items-center w-full">
        <button
          onClick={() => onPageChange(pagination.currentPage - 1)}
          disabled={!pagination.hasPrevPage}
          className="inline-flex items-center px-3 py-2 border border-brand-100 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        <span className="text-sm text-gray-500">
          Page {pagination.currentPage} of {pagination.totalPages}
        </span>
        <button
          onClick={() => onPageChange(pagination.currentPage + 1)}
          disabled={!pagination.hasNextPage}
          className="inline-flex items-center px-3 py-2 border border-brand-100 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Next
        </button>
      </div>

      {/* Desktop Pagination */}
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-500">
            Showing{' '}
            <span className="font-medium text-brand-900">
              {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}
            </span>{' '}
            to{' '}
            <span className="font-medium text-brand-900">
              {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
            </span>{' '}
            of{' '}
            <span className="font-medium text-brand-900">{pagination.totalItems}</span>{' '}
            results
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-xl overflow-hidden shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => onPageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="relative inline-flex items-center px-3 py-2 border border-brand-100 bg-white text-sm font-medium text-gray-500 hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>

            {/* Page Numbers - Show max 5 pages */}
            {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
              let page;
              if (pagination.totalPages <= 5) {
                page = i + 1;
              } else if (pagination.currentPage <= 3) {
                page = i + 1;
              } else if (pagination.currentPage >= pagination.totalPages - 2) {
                page = pagination.totalPages - 4 + i;
              } else {
                page = pagination.currentPage - 2 + i;
              }

              return (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors ${
                    page === pagination.currentPage
                      ? 'z-10 bg-brand-500 border-brand-500 text-white'
                      : 'bg-white border-brand-100 text-gray-500 hover:bg-brand-50/50'
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => onPageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="relative inline-flex items-center px-3 py-2 border border-brand-100 bg-white text-sm font-medium text-gray-500 hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

const Reports = () => {
  const [summaryData, setSummaryData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [partyData, setPartyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFY, setSelectedFY] = useState(getCurrentFY());
  const [filters, setFilters] = useState({
    company: null,
    party: null
  });

  // Main pagination states
  const [companyPagination, setCompanyPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [partyPagination, setPartyPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  // State for tracking expanded rows
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  const [expandedParties, setExpandedParties] = useState(new Set());

  // State for tracking pagination of bills and payments within each company/party
  const [companyBillsPagination, setCompanyBillsPagination] = useState({});
  const [companyPaymentsPagination, setCompanyPaymentsPagination] = useState({});
  const [partyBillsPagination, setPartyBillsPagination] = useState({});
  const [partyPaymentsPagination, setPartyPaymentsPagination] = useState({});

  useEffect(() => {
    fetchAllData();
  }, [filters, companyPagination.currentPage, partyPagination.currentPage, selectedFY]);

  // Reset pagination when filters change
  useEffect(() => {
    setCompanyPagination(prev => ({ ...prev, currentPage: 1 }));
    setPartyPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [filters, selectedFY]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      // Build common params
      const params = {};
      if (filters.company) params.company = filters.company._id;
      if (filters.party) params.party = filters.party._id;
      if (selectedFY) params.fy = selectedFY;

      // Fetch all data in parallel
      const [summaryResponse, companyResponse, partyResponse] = await Promise.all([
        reportsAPI.getSummary(params),
        reportsAPI.getCompanyBreakdown({
          ...params,
          page: companyPagination.currentPage,
          limit: companyPagination.itemsPerPage,
          billsPage: 1,
          billsLimit: 10,
          paymentsPage: 1,
          paymentsLimit: 10
        }),
        reportsAPI.getPartyBreakdown({
          ...params,
          page: partyPagination.currentPage,
          limit: partyPagination.itemsPerPage,
          billsPage: 1,
          billsLimit: 10,
          paymentsPage: 1,
          paymentsLimit: 10
        })
      ]);

      setSummaryData(summaryResponse.data);
      setCompanyData(companyResponse.data);
      setPartyData(partyResponse.data);

      // Update pagination states
      if (companyResponse.data.pagination) {
        setCompanyPagination(prev => ({
          ...prev,
          totalPages: companyResponse.data.pagination.totalPages,
          totalItems: companyResponse.data.pagination.totalItems,
          hasNextPage: companyResponse.data.pagination.hasNextPage,
          hasPrevPage: companyResponse.data.pagination.hasPrevPage
        }));
      }

      if (partyResponse.data.pagination) {
        setPartyPagination(prev => ({
          ...prev,
          totalPages: partyResponse.data.pagination.totalPages,
          totalItems: partyResponse.data.pagination.totalItems,
          hasNextPage: partyResponse.data.pagination.hasNextPage,
          hasPrevPage: partyResponse.data.pagination.hasPrevPage
        }));
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleCompanySelect = (selectedOption) => {
    const company = selectedOption ? selectedOption.data : null;
    setFilters(prev => ({
      ...prev,
      company: company
    }));
    setCompanyPagination(prev => ({ ...prev, currentPage: 1 }));
    setPartyPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePartySelect = (selectedOption) => {
    const party = selectedOption ? selectedOption.data : null;
    setFilters(prev => ({
      ...prev,
      party: party
    }));
    setCompanyPagination(prev => ({ ...prev, currentPage: 1 }));
    setPartyPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      company: null,
      party: null
    });
    setCompanyPagination(prev => ({ ...prev, currentPage: 1 }));
    setPartyPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Functions to handle expanding/collapsing rows
  const toggleCompanyExpansion = (companyId) => {
    setExpandedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  const togglePartyExpansion = (partyId) => {
    setExpandedParties(prev => {
      const newSet = new Set(prev);
      if (newSet.has(partyId)) {
        newSet.delete(partyId);
      } else {
        newSet.add(partyId);
      }
      return newSet;
    });
  };

  // Simple pagination handlers
  const handleCompanyPageChange = (page) => {
    setCompanyPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handlePartyPageChange = (page) => {
    setPartyPagination(prev => ({ ...prev, currentPage: page }));
  };

  // Functions to handle pagination for bills and payments within companies
  const handleCompanyBillsPageChange = async (companyId, page) => {
    setCompanyBillsPagination(prev => ({
      ...prev,
      [companyId]: { ...prev[companyId], currentPage: page }
    }));

    // Fetch updated data for this specific company
    await fetchCompanyData(companyId, page, companyPaymentsPagination[companyId]?.currentPage || 1);
  };

  const handleCompanyPaymentsPageChange = async (companyId, page) => {
    setCompanyPaymentsPagination(prev => ({
      ...prev,
      [companyId]: { ...prev[companyId], currentPage: page }
    }));

    // Fetch updated data for this specific company
    await fetchCompanyData(companyId, companyBillsPagination[companyId]?.currentPage || 1, page);
  };

  // Functions to handle pagination for bills and payments within parties
  const handlePartyBillsPageChange = async (partyId, page) => {
    setPartyBillsPagination(prev => ({
      ...prev,
      [partyId]: { ...prev[partyId], currentPage: page }
    }));

    // Fetch updated data for this specific party
    await fetchPartyData(partyId, page, partyPaymentsPagination[partyId]?.currentPage || 1);
  };

  const handlePartyPaymentsPageChange = async (partyId, page) => {
    setPartyPaymentsPagination(prev => ({
      ...prev,
      [partyId]: { ...prev[partyId], currentPage: page }
    }));

    // Fetch updated data for this specific party
    await fetchPartyData(partyId, partyBillsPagination[partyId]?.currentPage || 1, page);
  };

  // Function to fetch data for a specific company
  const fetchCompanyData = async (companyId, billsPage = 1, paymentsPage = 1) => {
    try {
      const params = {};
      if (filters.company) params.company = filters.company._id;
      if (filters.party) params.party = filters.party._id;
      if (selectedFY) params.fy = selectedFY;

      const response = await reportsAPI.getCompanyBreakdown({
        ...params,
        page: companyPagination.currentPage,
        limit: companyPagination.itemsPerPage,
        billsPage,
        billsLimit: 10,
        paymentsPage,
        paymentsLimit: 10
      });

      setCompanyData(response.data);
    } catch (error) {
      console.error('Error fetching company data:', error);
    }
  };

  // Function to fetch data for a specific party
  const fetchPartyData = async (partyId, billsPage = 1, paymentsPage = 1) => {
    try {
      const params = {};
      if (filters.company) params.company = filters.company._id;
      if (filters.party) params.party = filters.party._id;
      if (selectedFY) params.fy = selectedFY;

      const response = await reportsAPI.getPartyBreakdown({
        ...params,
        page: partyPagination.currentPage,
        limit: partyPagination.itemsPerPage,
        billsPage,
        billsLimit: 10,
        paymentsPage,
        paymentsLimit: 10
      });

      setPartyData(response.data);
    } catch (error) {
      console.error('Error fetching party data:', error);
    }
  };

  // PDF Download Functions
  const handleDownloadCompanyReport = () => {
    if (!companyData) return;

    const doc = generateCompanyReportPDF(companyData, summaryData);
    const fileName = `Company_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    downloadPDF(doc, fileName);
  };

  const handleDownloadPartyReport = () => {
    if (!partyData) return;

    const doc = generatePartyReportPDF(partyData, summaryData);
    const fileName = `Party_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    downloadPDF(doc, fileName);
  };

  const handleDownloadFilteredReport = () => {
    const doc = generateFilteredReportPDF(companyData, partyData, summaryData, filters);
    const fileName = `Filtered_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    downloadPDF(doc, fileName);
  };

  // Download PDF for specific company
  const handleDownloadCompanyPDF = async (companyId) => {
    try {
      console.log('Starting company PDF download for:', companyId);
      const params = {};
      if (filters.party) params.party = filters.party._id;
      if (selectedFY) params.fy = selectedFY;

      console.log('Calling API with params:', params);
      const response = await reportsAPI.downloadCompanyPDF(companyId, params);
      console.log('API response received:', response);

      const blob = new Blob([response.data], { type: 'application/pdf' });
      downloadBlob(blob, `Company_Report_${companyId}_${new Date().toISOString().split('T')[0]}.pdf`);
      console.log('PDF download completed successfully');
    } catch (error) {
      console.error('Error downloading company PDF:', error);
    }
  };

  // Download PDF for specific party
  const handleDownloadPartyPDF = async (partyId) => {
    try {
      console.log('Starting party PDF download for:', partyId);
      const params = {};
      if (filters.company) params.company = filters.company._id;
      if (selectedFY) params.fy = selectedFY;

      console.log('Calling API with params:', params);
      const response = await reportsAPI.downloadPartyPDF(partyId, params);
      console.log('API response received:', response);

      const blob = new Blob([response.data], { type: 'application/pdf' });
      downloadBlob(blob, `Party_Report_${partyId}_${new Date().toISOString().split('T')[0]}.pdf`);
      console.log('PDF download completed successfully');
    } catch (error) {
      console.error('Error downloading party PDF:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-brand-900">Reports</h1>
            <p className="text-gray-500 mt-1 sm:mt-2 text-sm sm:text-base">View pending amounts and financial reports</p>
          </div>
          <FYPicker value={selectedFY} onChange={setSelectedFY} />
        </div>

        {/* Download Buttons */}
        <div className="flex flex-wrap gap-2">
          {companyData && companyData.companyBreakdown && companyData.companyBreakdown.length > 0 && (
            <button
              onClick={handleDownloadCompanyReport}
              className="inline-flex items-center px-4 py-2.5 text-sm font-medium rounded-xl text-white bg-brand-500 hover:bg-brand-600 shadow-sm shadow-brand-500/25 transition-all duration-200"
            >
              <FileDown className="w-4 h-4 mr-2" /> Company Report
            </button>
          )}

          {partyData && partyData.partyBreakdown && partyData.partyBreakdown.length > 0 && (
            <button
              onClick={handleDownloadPartyReport}
              className="inline-flex items-center px-4 py-2.5 text-sm font-medium rounded-xl text-white bg-brand-500 hover:bg-brand-600 shadow-sm shadow-brand-500/25 transition-all duration-200"
            >
              <Users className="w-4 h-4 mr-2" /> Party Report
            </button>
          )}

          {(summaryData || companyData || partyData) && (
            <button
              onClick={handleDownloadFilteredReport}
              className="inline-flex items-center px-4 py-2.5 border border-brand-100 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50/50 transition-all duration-200"
            >
              <BarChart3 className="w-4 h-4 mr-2" /> Full Report
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <ReportFilterSection
        filters={filters}
        onCompanySelect={handleCompanySelect}
        onPartySelect={handlePartySelect}
        onClearFilters={clearFilters}
      />

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-32">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-500">Loading reports...</span>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summaryData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-brand-100 p-5 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-500/5 transition-all duration-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-100 text-blue-600">
                <FileText className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500">Total Bills</p>
                <p className="text-xl font-bold text-brand-900 tabular-nums">
                  ₹{summaryData.summary.totalBills.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-brand-100 p-5 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-500/5 transition-all duration-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald-100 text-emerald-600">
                <Wallet className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500">Total Payments</p>
                <p className="text-xl font-bold text-brand-900 tabular-nums">
                  ₹{summaryData.summary.totalPayments.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-brand-100 p-5 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-500/5 transition-all duration-200">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${summaryData.summary.pendingAmount > 0 ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-500'}`}>
                <Clock className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500">Pending Amount</p>
                <p className={`text-xl font-bold tabular-nums ${summaryData.summary.pendingAmount > 0 ? 'text-amber-600' : 'text-brand-900'}`}>
                  ₹{summaryData.summary.pendingAmount.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Breakdown */}
      {companyData && companyData.companyBreakdown && companyData.companyBreakdown.length > 0 && !filters.company && (
        <div className="bg-white rounded-2xl border border-brand-100 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-brand-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100 text-blue-600">
              <Building2 className="w-4 h-4" />
            </div>
            <h2 className="text-lg font-semibold text-brand-900">Company-wise Pending Amounts</h2>
          </div>

          {/* Mobile Card View */}
          <div className="block sm:hidden">
            {companyData.companyBreakdown
              .sort((a, b) => b.pendingAmount - a.pendingAmount)
              .map((company, index) => (
                <div key={index} className="border-b border-brand-100 last:border-b-0">
                  {/* Company Summary Card */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-brand-900">
                        {company.companyName}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleCompanyExpansion(company.companyId)}
                          className="bg-brand-50 text-brand-600 rounded-lg px-2.5 py-1 text-xs font-medium hover:bg-brand-100 transition-colors inline-flex items-center gap-1"
                        >
                          {expandedCompanies.has(company.companyId) ? (
                            <><ChevronUp className="w-3 h-3" /> Hide</>
                          ) : (
                            <><ChevronDown className="w-3 h-3" /> Details</>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            console.log('Company PDF button clicked (mobile) for:', company.companyId);
                            handleDownloadCompanyPDF(company.companyId);
                          }}
                          className="bg-brand-50 text-brand-600 rounded-lg px-2.5 py-1 text-xs font-medium hover:bg-brand-100 transition-colors inline-flex items-center gap-1"
                          title="Download PDF Report"
                        >
                          <FileDown className="w-3 h-3" /> PDF
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                      <div>
                        <span className="font-medium">Bills:</span> <span className="tabular-nums">₹{company.totalBills.toLocaleString('en-IN')}</span>
                      </div>
                      <div>
                        <span className="font-medium">Payments:</span> <span className="tabular-nums">₹{company.totalPayments.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="text-xs font-medium text-gray-500">Pending:</span>
                      <span className={`text-sm font-semibold ml-1 tabular-nums ${company.pendingAmount > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        ₹{company.pendingAmount.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>

                  {/* Expanded Bills Table */}
                  {expandedCompanies.has(company.companyId) && (
                    <div className="px-4 pb-4 bg-brand-50/30">
                      <div className="text-xs font-medium text-gray-500 mb-2">Bills & Payments:</div>
                      <div className="space-y-2">
                        {/* Bills */}
                        {company.bills && company.bills.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-brand-600 mb-1">Bills:</div>
                            {company.bills.map((bill, billIndex) => (
                              <div key={billIndex} className="bg-white p-2.5 rounded-xl border border-brand-100 text-xs mb-1.5">
                                <div className="flex justify-between items-center mb-1">
                                  <div>
                                    <div className="font-medium text-brand-900">{formatDate(bill.billDate)}</div>
                                    <span className="font-medium text-brand-900">Bill #{bill.billNumber}</span>
                                    <span className="text-gray-500 ml-2">({bill.party?.name || 'No Party'})</span>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-semibold text-brand-900 tabular-nums">₹{bill.amount.toLocaleString('en-IN')}</div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="text-emerald-600 font-medium">Paid:</span> <span className="tabular-nums">₹{(bill.paidAmount || 0).toLocaleString('en-IN')}</span>
                                  </div>
                                  <div>
                                    <span className="text-amber-600 font-medium">Pending:</span> <span className="tabular-nums">₹{(bill.pendingAmount || 0).toLocaleString('en-IN')}</span>
                                  </div>
                                </div>
                                <div className="mt-1">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    bill.status === 'Paid'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {bill.status}
                                  </span>
                                </div>
                              </div>
                            ))}

                            {/* Mobile Bills Pagination */}
                            {company.billsPagination && company.billsPagination.totalPages > 1 && (
                              <div className="mt-3 flex justify-between items-center">
                                <button
                                  onClick={() => handleCompanyBillsPageChange(company.companyId, company.billsPagination.currentPage - 1)}
                                  disabled={!company.billsPagination.hasPrevPage}
                                  className="inline-flex items-center px-3 py-2 border border-brand-100 text-xs font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Previous
                                </button>
                                <span className="text-xs text-gray-500">
                                  Page {company.billsPagination.currentPage} of {company.billsPagination.totalPages}
                                </span>
                                <button
                                  onClick={() => handleCompanyBillsPageChange(company.companyId, company.billsPagination.currentPage + 1)}
                                  disabled={!company.billsPagination.hasNextPage}
                                  className="inline-flex items-center px-3 py-2 border border-brand-100 text-xs font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Payments */}
                        {company.payments && company.payments.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-emerald-600 mb-1">Payments:</div>
                            {company.payments.map((payment, paymentIndex) => (
                              <div key={paymentIndex} className="bg-white p-2.5 rounded-xl border border-brand-100 text-xs mb-1.5">
                                <div className="flex justify-between items-center mb-1">
                                  <div>
                                    <div className="font-medium text-brand-900">{formatDate(payment.paymentDate)}</div>
                                    <span className="font-medium text-brand-900">Payment #{payment.paymentNumber}</span>
                                    <span className="text-gray-500 ml-2">({payment.party?.name || 'No Party'})</span>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-semibold text-brand-900 tabular-nums">₹{payment.amount.toLocaleString('en-IN')}</div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="text-emerald-600 font-medium">Paid:</span> <span className="tabular-nums">₹{(payment.paidAmount || 0).toLocaleString('en-IN')}</span>
                                  </div>
                                  <div>
                                    <span className="text-amber-600 font-medium">Pending:</span> <span className="tabular-nums">₹{(payment.pendingAmount || 0).toLocaleString('en-IN')}</span>
                                  </div>
                                </div>
                                <div className="mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                    {payment.status}
                                  </span>
                                </div>
                              </div>
                            ))}

                            {/* Mobile Payments Pagination */}
                            {company.paymentsPagination && company.paymentsPagination.totalPages > 1 && (
                              <div className="mt-3 flex justify-between items-center">
                                <button
                                  onClick={() => handleCompanyPaymentsPageChange(company.companyId, company.paymentsPagination.currentPage - 1)}
                                  disabled={!company.paymentsPagination.hasPrevPage}
                                  className="inline-flex items-center px-3 py-2 border border-brand-100 text-xs font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Previous
                                </button>
                                <span className="text-xs text-gray-500">
                                  Page {company.paymentsPagination.currentPage} of {company.paymentsPagination.totalPages}
                                </span>
                                <button
                                  onClick={() => handleCompanyPaymentsPageChange(company.companyId, company.paymentsPagination.currentPage + 1)}
                                  disabled={!company.paymentsPagination.hasNextPage}
                                  className="inline-flex items-center px-3 py-2 border border-brand-100 text-xs font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {(!company.bills || company.bills.length === 0) && (!company.payments || company.payments.length === 0) && (
                          <div className="text-xs text-gray-500 text-center py-2">No bills or payments found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-brand-100">
              <thead className="bg-brand-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Bills
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Payments
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pending Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-brand-100">
                {companyData.companyBreakdown
                  .sort((a, b) => b.pendingAmount - a.pendingAmount)
                  .map((company, index) => (
                    <React.Fragment key={index}>
                      {/* Company Summary Row */}
                      <tr className="hover:bg-brand-50/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-brand-900">
                            {company.companyName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-brand-900 tabular-nums">
                            ₹{company.totalBills.toLocaleString('en-IN')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-brand-900 tabular-nums">
                            ₹{company.totalPayments.toLocaleString('en-IN')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm font-semibold tabular-nums ${company.pendingAmount > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                            ₹{company.pendingAmount.toLocaleString('en-IN')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleCompanyExpansion(company.companyId)}
                              className="bg-brand-50 text-brand-600 rounded-lg px-2.5 py-1 text-xs font-medium hover:bg-brand-100 transition-colors inline-flex items-center gap-1"
                            >
                              {expandedCompanies.has(company.companyId) ? (
                                <><ChevronUp className="w-3 h-3" /> Hide</>
                              ) : (
                                <><ChevronDown className="w-3 h-3" /> Details</>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                console.log('Company PDF button clicked for:', company.companyId);
                                handleDownloadCompanyPDF(company.companyId);
                              }}
                              className="bg-brand-50 text-brand-600 rounded-lg px-2.5 py-1 text-xs font-medium hover:bg-brand-100 transition-colors inline-flex items-center gap-1"
                              title="Download PDF Report"
                            >
                              <FileDown className="w-3 h-3" /> PDF
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Bills Table Row */}
                      {expandedCompanies.has(company.companyId) && (
                        <tr>
                          <td colSpan="5" className="px-6 py-4 bg-brand-50/30">
                            <div className="space-y-4">
                              {/* Bills Section */}
                              {company.bills && company.bills.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-brand-600 mb-2">Bills</h4>
                                  <div className="overflow-x-auto rounded-xl border border-brand-100">
                                    <table className="min-w-full divide-y divide-brand-100">
                                      <thead className="bg-brand-50/50">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Party</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill #</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-brand-100">
                                        {company.bills.map((bill, billIndex) => (
                                          <tr key={billIndex} className="hover:bg-brand-50/50 transition-colors">
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                              {new Date(bill.billDate).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric'
                                              })}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                              {bill.party?.name || 'No Party'}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-brand-900 font-medium">
                                              {bill.billNumber}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-brand-900 tabular-nums">
                                              ₹{bill.amount.toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-emerald-600 tabular-nums">
                                              ₹{(bill.paidAmount || 0).toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-amber-600 tabular-nums">
                                              ₹{(bill.pendingAmount || 0).toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                bill.status === 'Paid'
                                                  ? 'bg-emerald-100 text-emerald-700'
                                                  : 'bg-amber-100 text-amber-700'
                                              }`}>
                                                {bill.status}
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>

                                  {/* Bills Pagination */}
                                  {company.billsPagination && company.billsPagination.totalPages > 1 && (
                                    <PaginationControls
                                      pagination={company.billsPagination}
                                      onPageChange={(page) => handleCompanyBillsPageChange(company.companyId, page)}
                                      className="mt-4"
                                    />
                                  )}
                                </div>
                              )}

                              {/* Payments Section */}
                              {company.payments && company.payments.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-emerald-600 mb-2">Payments</h4>
                                  <div className="overflow-x-auto rounded-xl border border-brand-100">
                                    <table className="min-w-full divide-y divide-brand-100">
                                      <thead className="bg-brand-50/50">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment #</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Party</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-brand-100">
                                        {company.payments.map((payment, paymentIndex) => (
                                          <tr key={paymentIndex} className="hover:bg-brand-50/50 transition-colors">
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-brand-900 font-medium">
                                              {payment.paymentNumber}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                              {payment.party?.name || 'No Party'}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                              {formatDate(payment.paymentDate)}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-brand-900 tabular-nums">
                                              ₹{payment.amount.toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-emerald-600 tabular-nums">
                                              ₹{(payment.paidAmount || 0).toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-amber-600 tabular-nums">
                                              ₹{(payment.pendingAmount || 0).toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                                {payment.status}
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>

                                  {/* Payments Pagination */}
                                  {company.paymentsPagination && company.paymentsPagination.totalPages > 1 && (
                                    <PaginationControls
                                      pagination={company.paymentsPagination}
                                      onPageChange={(page) => handleCompanyPaymentsPageChange(company.companyId, page)}
                                      className="mt-4"
                                    />
                                  )}
                                </div>
                              )}

                              {(!company.bills || company.bills.length === 0) && (!company.payments || company.payments.length === 0) && (
                                <div className="text-center py-4 text-gray-500">
                                  No bills or payments found for this company
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Company Breakdown Pagination */}
          <PaginationControls
            pagination={companyPagination}
            onPageChange={handleCompanyPageChange}
          />
        </div>
      )}

      {/* Party Breakdown */}
      {partyData && partyData.partyBreakdown && partyData.partyBreakdown.length > 0 && !filters.party && (
        <div className="bg-white rounded-2xl border border-brand-100 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-brand-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-100 text-emerald-600">
              <Users className="w-4 h-4" />
            </div>
            <h2 className="text-lg font-semibold text-brand-900">Party-wise Pending Amounts</h2>
          </div>

          {/* Mobile Card View */}
          <div className="block sm:hidden">
            {partyData.partyBreakdown
              .sort((a, b) => b.pendingAmount - a.pendingAmount)
              .map((party, index) => (
                <div key={index} className="border-b border-brand-100 last:border-b-0">
                  {/* Party Summary Card */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-brand-900">
                        {party.partyName}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => togglePartyExpansion(party.partyId)}
                          className="bg-brand-50 text-brand-600 rounded-lg px-2.5 py-1 text-xs font-medium hover:bg-brand-100 transition-colors inline-flex items-center gap-1"
                        >
                          {expandedParties.has(party.partyId) ? (
                            <><ChevronUp className="w-3 h-3" /> Hide</>
                          ) : (
                            <><ChevronDown className="w-3 h-3" /> Details</>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            console.log('Party PDF button clicked (mobile) for:', party.partyId);
                            handleDownloadPartyPDF(party.partyId);
                          }}
                          className="bg-brand-50 text-brand-600 rounded-lg px-2.5 py-1 text-xs font-medium hover:bg-brand-100 transition-colors inline-flex items-center gap-1"
                          title="Download PDF Report"
                        >
                          <FileDown className="w-3 h-3" /> PDF
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                      <div>
                        <span className="font-medium">Bills:</span> <span className="tabular-nums">₹{party.totalBills.toLocaleString('en-IN')}</span>
                      </div>
                      <div>
                        <span className="font-medium">Payments:</span> <span className="tabular-nums">₹{party.totalPayments.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="text-xs font-medium text-gray-500">Pending:</span>
                      <span className={`text-sm font-semibold ml-1 tabular-nums ${party.pendingAmount > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        ₹{party.pendingAmount.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>

                  {/* Expanded Bills Table */}
                  {expandedParties.has(party.partyId) && (
                    <div className="px-4 pb-4 bg-brand-50/30">
                      <div className="text-xs font-medium text-gray-500 mb-2">Bills & Payments:</div>
                      <div className="space-y-2">
                        {/* Bills */}
                        {party.bills && party.bills.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-brand-600 mb-1">Bills:</div>
                            {party.bills.map((bill, billIndex) => (
                              <div key={billIndex} className="bg-white p-2.5 rounded-xl border border-brand-100 text-xs mb-1.5">
                                <div className="flex justify-between items-center mb-1">
                                  <div>
                                    <span className="font-medium text-brand-900">Bill #{bill.billNumber}</span>
                                    <span className="text-gray-500 ml-2">({bill.company?.name || 'No Company'})</span>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-semibold text-brand-900 tabular-nums">₹{bill.amount.toLocaleString('en-IN')}</div>
                                    <div className="text-gray-500">{new Date(bill.billDate).toLocaleDateString('en-GB', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })}</div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="text-emerald-600 font-medium">Paid:</span> <span className="tabular-nums">₹{(bill.paidAmount || 0).toLocaleString('en-IN')}</span>
                                  </div>
                                  <div>
                                    <span className="text-amber-600 font-medium">Pending:</span> <span className="tabular-nums">₹{(bill.pendingAmount || 0).toLocaleString('en-IN')}</span>
                                  </div>
                                </div>
                                <div className="mt-1">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    bill.status === 'Paid'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    {bill.status}
                                  </span>
                                </div>
                              </div>
                            ))}

                            {/* Mobile Bills Pagination */}
                            {party.billsPagination && party.billsPagination.totalPages > 1 && (
                              <div className="mt-3 flex justify-between items-center">
                                <button
                                  onClick={() => handlePartyBillsPageChange(party.partyId, party.billsPagination.currentPage - 1)}
                                  disabled={!party.billsPagination.hasPrevPage}
                                  className="inline-flex items-center px-3 py-2 border border-brand-100 text-xs font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Previous
                                </button>
                                <span className="text-xs text-gray-500">
                                  Page {party.billsPagination.currentPage} of {party.billsPagination.totalPages}
                                </span>
                                <button
                                  onClick={() => handlePartyBillsPageChange(party.partyId, party.billsPagination.currentPage + 1)}
                                  disabled={!party.billsPagination.hasNextPage}
                                  className="inline-flex items-center px-3 py-2 border border-brand-100 text-xs font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Payments */}
                        {party.payments && party.payments.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-emerald-600 mb-1">Payments:</div>
                            {party.payments.map((payment, paymentIndex) => (
                              <div key={paymentIndex} className="bg-white p-2.5 rounded-xl border border-brand-100 text-xs mb-1.5">
                                <div className="flex justify-between items-center mb-1">
                                  <div>
                                    <span className="font-medium text-brand-900">Payment #{payment.paymentNumber}</span>
                                    <span className="text-gray-500 ml-2">({payment.company?.name || 'No Company'})</span>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-semibold text-brand-900 tabular-nums">₹{payment.amount.toLocaleString('en-IN')}</div>
                                    <div className="text-gray-500">{formatDate(payment.paymentDate)}</div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="text-emerald-600 font-medium">Paid:</span> <span className="tabular-nums">₹{(payment.paidAmount || 0).toLocaleString('en-IN')}</span>
                                  </div>
                                  <div>
                                    <span className="text-amber-600 font-medium">Pending:</span> <span className="tabular-nums">₹{(payment.pendingAmount || 0).toLocaleString('en-IN')}</span>
                                  </div>
                                </div>
                                <div className="mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                    {payment.status}
                                  </span>
                                </div>
                              </div>
                            ))}

                            {/* Mobile Payments Pagination */}
                            {party.paymentsPagination && party.paymentsPagination.totalPages > 1 && (
                              <div className="mt-3 flex justify-between items-center">
                                <button
                                  onClick={() => handlePartyPaymentsPageChange(party.partyId, party.paymentsPagination.currentPage - 1)}
                                  disabled={!party.paymentsPagination.hasPrevPage}
                                  className="inline-flex items-center px-3 py-2 border border-brand-100 text-xs font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Previous
                                </button>
                                <span className="text-xs text-gray-500">
                                  Page {party.paymentsPagination.currentPage} of {party.paymentsPagination.totalPages}
                                </span>
                                <button
                                  onClick={() => handlePartyPaymentsPageChange(party.partyId, party.paymentsPagination.currentPage + 1)}
                                  disabled={!party.paymentsPagination.hasNextPage}
                                  className="inline-flex items-center px-3 py-2 border border-brand-100 text-xs font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {(!party.bills || party.bills.length === 0) && (!party.payments || party.payments.length === 0) && (
                          <div className="text-xs text-gray-500 text-center py-2">No bills or payments found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-brand-100">
              <thead className="bg-brand-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Party Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Bills
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Payments
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pending Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-brand-100">
                {partyData.partyBreakdown
                  .sort((a, b) => b.pendingAmount - a.pendingAmount)
                  .map((party, index) => (
                    <React.Fragment key={index}>
                      {/* Party Summary Row */}
                      <tr className="hover:bg-brand-50/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-brand-900">
                            {party.partyName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-brand-900 tabular-nums">
                            ₹{party.totalBills.toLocaleString('en-IN')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-brand-900 tabular-nums">
                            ₹{party.totalPayments.toLocaleString('en-IN')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm font-semibold tabular-nums ${party.pendingAmount > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                            ₹{party.pendingAmount.toLocaleString('en-IN')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => togglePartyExpansion(party.partyId)}
                              className="bg-brand-50 text-brand-600 rounded-lg px-2.5 py-1 text-xs font-medium hover:bg-brand-100 transition-colors inline-flex items-center gap-1"
                            >
                              {expandedParties.has(party.partyId) ? (
                                <><ChevronUp className="w-3 h-3" /> Hide</>
                              ) : (
                                <><ChevronDown className="w-3 h-3" /> Details</>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                console.log('Party PDF button clicked for:', party.partyId);
                                handleDownloadPartyPDF(party.partyId);
                              }}
                              className="bg-brand-50 text-brand-600 rounded-lg px-2.5 py-1 text-xs font-medium hover:bg-brand-100 transition-colors inline-flex items-center gap-1"
                              title="Download PDF Report"
                            >
                              <FileDown className="w-3 h-3" /> PDF
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Bills Table Row */}
                      {expandedParties.has(party.partyId) && (
                        <tr>
                          <td colSpan="5" className="px-6 py-4 bg-brand-50/30">
                            <div className="space-y-4">
                              {/* Bills Section */}
                              {party.bills && party.bills.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-brand-600 mb-2">Bills</h4>
                                  <div className="overflow-x-auto rounded-xl border border-brand-100">
                                    <table className="min-w-full divide-y divide-brand-100">
                                      <thead className="bg-brand-50/50">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill #</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-brand-100">
                                        {party.bills.map((bill, billIndex) => (
                                          <tr key={billIndex} className="hover:bg-brand-50/50 transition-colors">
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                              {new Date(bill.billDate).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric'
                                              })}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                              {bill.company?.name || 'No Company'}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-brand-900 font-medium">
                                              {bill.billNumber}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-brand-900 tabular-nums">
                                              ₹{bill.amount.toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-emerald-600 tabular-nums">
                                              ₹{(bill.paidAmount || 0).toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-amber-600 tabular-nums">
                                              ₹{(bill.pendingAmount || 0).toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                bill.status === 'Paid'
                                                  ? 'bg-emerald-100 text-emerald-700'
                                                  : 'bg-amber-100 text-amber-700'
                                              }`}>
                                                {bill.status}
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>

                                  {/* Bills Pagination */}
                                  {party.billsPagination && party.billsPagination.totalPages > 1 && (
                                    <PaginationControls
                                      pagination={party.billsPagination}
                                      onPageChange={(page) => handlePartyBillsPageChange(party.partyId, page)}
                                      className="mt-4"
                                    />
                                  )}
                                </div>
                              )}

                              {/* Payments Section */}
                              {party.payments && party.payments.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-emerald-600 mb-2">Payments</h4>
                                  <div className="overflow-x-auto rounded-xl border border-brand-100">
                                    <table className="min-w-full divide-y divide-brand-100">
                                      <thead className="bg-brand-50/50">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment #</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Amount</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-brand-100">
                                        {party.payments.map((payment, paymentIndex) => (
                                          <tr key={paymentIndex} className="hover:bg-brand-50/50 transition-colors">
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-brand-900 font-medium">
                                              {payment.paymentNumber}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                              {payment.company?.name || 'No Company'}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                              {formatDate(payment.paymentDate)}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-brand-900 tabular-nums">
                                              ₹{payment.amount.toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-emerald-600 tabular-nums">
                                              ₹{(payment.paidAmount || 0).toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-amber-600 tabular-nums">
                                              ₹{(payment.pendingAmount || 0).toLocaleString('en-IN')}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                                {payment.status}
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>

                                  {/* Payments Pagination */}
                                  {party.paymentsPagination && party.paymentsPagination.totalPages > 1 && (
                                    <PaginationControls
                                      pagination={party.paymentsPagination}
                                      onPageChange={(page) => handlePartyPaymentsPageChange(party.partyId, page)}
                                      className="mt-4"
                                    />
                                  )}
                                </div>
                              )}

                              {(!party.bills || party.bills.length === 0) && (!party.payments || party.payments.length === 0) && (
                                <div className="text-center py-4 text-gray-500">
                                  No bills or payments found for this party
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Party Breakdown Pagination */}
          <PaginationControls
            pagination={partyPagination}
            onPageChange={handlePartyPageChange}
          />
        </div>
      )}

      {/* No Data Message */}
      {summaryData &&
       (!companyData || !companyData.companyBreakdown || companyData.companyBreakdown.length === 0) &&
       (!partyData || !partyData.partyBreakdown || partyData.partyBreakdown.length === 0) && (
        <div className="bg-white rounded-2xl border border-brand-100 p-8 text-center">
          <div className="text-gray-500">
            <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-8 h-8 text-brand-400" />
            </div>
            <h3 className="text-lg font-semibold text-brand-900 mb-2">No Data Available</h3>
            <p className="text-gray-500">
              {filters.company || filters.party
                ? 'No data found for the selected filters.'
                : 'No bills or payments found. Add some data to see reports.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
